// PWA — 提交表单组件（Req 7.1, 7.2, 7.3, 7.4, 6.4 / 任务 9.4）
//
// 收集提示词、尺寸（默认 1216×832）、steps(20)、guidance(3.5)、seed/随机；
// 尺寸输入经 shared normalizeTo16 实时提示并在提交时调整（Req 7.3, 7.4）；
// status≠running 时经 submitGate 提示「请先启动实例」且不发起请求（Req 6.4），
// 否则 `POST /jobs` 并展示返回的 jobId/status（Req 7.1, 7.2）。
//
// 本组件为展示型：表单状态由上层 useSubmit Hook 提供（见 App.tsx），
// 与电源面板 / 认证共享同一 authedFetch 与电源状态源。

import type { UseSubmitResult } from "./useSubmit.js";

export interface SubmitFormProps {
  /** useSubmit 的返回值（由上层 App 持有并下传）。 */
  submit: UseSubmitResult;
  /** 是否已认证：未认证时提示先登录。 */
  authenticated: boolean;
}

export function SubmitForm({
  submit: s,
  authenticated,
}: SubmitFormProps): JSX.Element {
  const {
    values,
    setField,
    widthHint,
    heightHint,
    availability,
    submitting,
    error,
    result,
    randomizeSeed,
    submit,
  } = s;

  if (!authenticated) {
    return (
      <section className="card" aria-label="新建生成">
        <h2 className="card__title">新建生成</h2>
        <p className="app__hint">登录后可提交生成任务。</p>
      </section>
    );
  }

  const onNumber = (
    key: "width" | "height" | "steps" | "seed",
    raw: string,
  ) => {
    const n = Number(raw);
    setField(key, Number.isFinite(n) ? n : 0);
  };

  return (
    <section className="card" aria-label="新建生成">
      <h2 className="card__title">新建生成</h2>

      <form
        className="submit-form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {/* 提示词 */}
        <label className="submit-form__field">
          <span className="submit-form__label">提示词</span>
          <textarea
            className="submit-form__textarea"
            rows={3}
            value={values.positivePrompt}
            placeholder="描述想生成的画面…"
            onChange={(e) => setField("positivePrompt", e.target.value)}
          />
        </label>

        {/* 负向提示词（FLUX dev 通常留空） */}
        <label className="submit-form__field">
          <span className="submit-form__label">负向提示词（可选）</span>
          <textarea
            className="submit-form__textarea"
            rows={2}
            value={values.negativePrompt}
            placeholder="通常留空"
            onChange={(e) => setField("negativePrompt", e.target.value)}
          />
        </label>

        {/* 尺寸：宽 / 高，输入非 16 倍数时给出规范化提示（Req 7.4） */}
        <div className="submit-form__row">
          <label className="submit-form__field">
            <span className="submit-form__label">宽</span>
            <input
              className="submit-form__input"
              type="number"
              inputMode="numeric"
              min={16}
              step={16}
              value={values.width}
              onChange={(e) => onNumber("width", e.target.value)}
              onBlur={() => setField("width", widthHint.value)}
            />
            {widthHint.hint && (
              <span className="submit-form__hint" role="status">
                {widthHint.hint}
              </span>
            )}
          </label>

          <label className="submit-form__field">
            <span className="submit-form__label">高</span>
            <input
              className="submit-form__input"
              type="number"
              inputMode="numeric"
              min={16}
              step={16}
              value={values.height}
              onChange={(e) => onNumber("height", e.target.value)}
              onBlur={() => setField("height", heightHint.value)}
            />
            {heightHint.hint && (
              <span className="submit-form__hint" role="status">
                {heightHint.hint}
              </span>
            )}
          </label>
        </div>

        {/* steps / guidance */}
        <div className="submit-form__row">
          <label className="submit-form__field">
            <span className="submit-form__label">步数（steps）</span>
            <input
              className="submit-form__input"
              type="number"
              inputMode="numeric"
              min={1}
              value={values.steps}
              onChange={(e) => onNumber("steps", e.target.value)}
            />
          </label>

          <label className="submit-form__field">
            <span className="submit-form__label">引导（guidance）</span>
            <input
              className="submit-form__input"
              type="number"
              inputMode="decimal"
              step={0.1}
              min={0}
              value={values.guidance}
              onChange={(e) => {
                const n = Number(e.target.value);
                setField("guidance", Number.isFinite(n) ? n : 0);
              }}
            />
          </label>
        </div>

        {/* seed：随机 / 固定 */}
        <div className="submit-form__field">
          <span className="submit-form__label">随机数种子（seed）</span>
          <div className="submit-form__row submit-form__row--seed">
            <label className="submit-form__checkbox">
              <input
                type="checkbox"
                checked={values.seedMode === "random"}
                onChange={(e) =>
                  setField("seedMode", e.target.checked ? "random" : "fixed")
                }
              />
              <span>随机</span>
            </label>
            <input
              className="submit-form__input"
              type="number"
              inputMode="numeric"
              min={0}
              value={values.seed}
              disabled={values.seedMode === "random"}
              onChange={(e) => onNumber("seed", e.target.value)}
            />
            <button
              type="button"
              className="btn submit-form__dice"
              onClick={randomizeSeed}
              aria-label="生成随机种子"
            >
              掷骰
            </button>
          </div>
        </div>

        {/* 门控原因（非 running / 未填提示词） */}
        {!availability.canSubmit && availability.reason && (
          <p className="app__hint" role="status" style={{ textAlign: "left" }}>
            {availability.reason}
          </p>
        )}

        {error && (
          <p className="app__hint" role="alert" style={{ textAlign: "left" }}>
            {error}
          </p>
        )}

        {result && (
          <p
            className="submit-form__ok"
            role="status"
            aria-live="polite"
            style={{ textAlign: "left" }}
          >
            已提交 · 任务 {result.jobId}（{result.status}）
          </p>
        )}

        <button
          type="submit"
          className="btn btn--primary"
          disabled={!availability.canSubmit || submitting}
        >
          {submitting ? "提交中…" : "提交生成"}
        </button>
      </form>
    </section>
  );
}
